import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { cn } from "../../utils/bem";
import { Modal } from "../Modal/Modal";
import { currentPageName, detectBuild, detectSurface, guessModule } from "./testReportStore";
import "./TestReportModal.prefix.css";

const block = cn("test-report");

const KINDS = [
  { value: "failure", label: "Something did not work" },
  { value: "suggestion", label: "Suggestion or question" },
];

/**
 * Report form for the testing programme. Opens from anywhere; the title
 * is pre-filled with the page the tester is on, and the context (URL,
 * client, build, browser, viewport, locale) is collected automatically so
 * testers only describe what they saw. A "My reports" view shows what
 * they've sent and the GitHub issue each one became.
 */
export const TestReportModal = ({ config, preset = {}, onClose }) => {
  const api = useAPI();
  const toast = useToast();
  const pageName = useMemo(() => currentPageName(), []);
  const [view, setView] = useState("new"); // new | mine
  const [kind, setKind] = useState(preset.kind || "failure");
  const [stepId, setStepId] = useState(preset.step_id || "");
  const [module, setModule] = useState(preset.module || guessModule(config?.route_module_hints));
  const [surface, setSurface] = useState(detectSurface());
  const [severity, setSeverity] = useState("minor");
  const [title, setTitle] = useState(preset.title || "");
  const [did, setDid] = useState("");
  const [expected, setExpected] = useState("");
  const [happened, setHappened] = useState("");
  const [shot, setShot] = useState(null);
  const [shotUrl, setShotUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);
  const fileRef = useRef(null);
  const titleRef = useRef(null);
  const rootRef = useRef(null);
  // Height of the form view, measured live; My reports is given the same
  // height so switching views never resizes the modal, on any screen.
  const [formHeight, setFormHeight] = useState(null);

  useEffect(() => {
    if (view !== "new" || !rootRef.current) return;
    const el = rootRef.current;
    const measure = () => setFormHeight(Math.round(el.getBoundingClientRect().height));
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [view, kind]);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Screenshot: file picker, or paste an image anywhere in the form.
  const takeShot = useCallback(
    (file) => {
      if (!file || !file.type?.startsWith("image/")) return;
      if (file.size > 8 * 1024 * 1024) {
        toast.show({ message: "Screenshot is larger than 8 MB.", type: "error" });
        return;
      }
      setShot(file);
      setShotUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(file);
      });
    },
    [toast],
  );

  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
    if (item) {
      e.preventDefault();
      takeShot(item.getAsFile());
    }
  };

  useEffect(
    () => () => {
      if (shotUrl) URL.revokeObjectURL(shotUrl);
    },
    [shotUrl],
  );

  const loadMine = useCallback(async () => {
    const res = await api.callApi("testReportMine", { suppressError: true });
    setMine(res && !res.error ? res.results || [] : []);
  }, [api]);

  useEffect(() => {
    if (view === "mine" && mine === null) loadMine();
  }, [view, mine, loadMine]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (!title.trim()) {
      toast.show({ message: "Give it a short title.", type: "error" });
      titleRef.current?.focus();
      return;
    }
    if (kind === "failure" && !happened.trim()) {
      toast.show({ message: "Say what happened.", type: "error" });
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("step_id", stepId.trim());
    fd.append("module", module);
    fd.append("surface", surface);
    if (kind === "failure") fd.append("severity", severity);
    fd.append("title", title.trim());
    fd.append("did", did.trim());
    fd.append("expected", expected.trim());
    fd.append("happened", happened.trim());
    fd.append("page_title", pageName);
    fd.append("page_url", window.location.href);
    fd.append("build", detectBuild(config?.server_build));
    fd.append("user_agent", navigator.userAgent || "");
    fd.append("viewport", `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`);
    fd.append("locale", navigator.language || "");
    if (shot) fd.append("screenshot", shot, shot.name || "screenshot.png");
    const res = await api.callApi("testReportCreate", {
      body: fd,
      headers: { "Content-Type": "multipart/form-data" },
      suppressError: true,
    });
    setBusy(false);
    if (!res || res.error) {
      toast.show({ message: res?.detail || res?.error || "Could not send the report, try again.", type: "error" });
      return;
    }
    const filed = res.status === "filed" && res.github_issue_number;
    toast.show({
      message: filed
        ? `Thank you! Report filed as #${res.github_issue_number}.`
        : "Thank you! Your report is saved and will be filed shortly.",
    });
    // The checklist page refetches on this so a step failed via its
    // Fail button picks up the linked issue URL.
    window.dispatchEvent(new CustomEvent("afri:test-report-filed", { detail: { id: res.id, step_id: res.step_id } }));
    onClose?.();
  };

  const modules = config?.modules || [];
  const surfaces = config?.surfaces || [];
  const severities = config?.severities || [];

  return (
    <Modal visible onHide={onClose} title="Report a test step" style={{ width: 640 }}>
      <div
        ref={rootRef}
        className={block.toClassName()}
        onPaste={onPaste}
        style={view === "mine" && formHeight ? { height: formHeight } : undefined}
      >
        <div className={block.elem("views").toClassName()} role="group" aria-label="Report view">
          <button
            type="button"
            aria-pressed={view === "new"}
            className={block
              .elem("view")
              .mod({ active: view === "new" })
              .toClassName()}
            onClick={() => setView("new")}
          >
            New report
          </button>
          <button
            type="button"
            aria-pressed={view === "mine"}
            className={block
              .elem("view")
              .mod({ active: view === "mine" })
              .toClassName()}
            onClick={() => setView("mine")}
          >
            My reports
          </button>
        </div>
        <div className={block.elem("top").toClassName()}>
          <span className={block.elem("context").toClassName()}>
            Reporting from <b>{pageName}</b>
            {config?.round ? ` (round ${config.round})` : ""}
          </span>
        </div>
        {config?.intro && view === "new" && <p className={block.elem("intro").toClassName()}>{config.intro}</p>}

        {view === "mine" ? (
          <div className={block.elem("mine").toClassName()}>
            <div className={block.elem("mine-scroll").toClassName()}>
              {mine === null ? (
                <p className={block.elem("muted").toClassName()}>Loading…</p>
              ) : mine.length === 0 ? (
                <p className={block.elem("muted").toClassName()}>You have not sent any reports yet.</p>
              ) : (
                <table className={block.elem("table").toClassName()}>
                  <colgroup>
                    <col />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>When</th>
                      <th>Step</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mine.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.page_title ? `${r.page_title}: ` : ""}
                          {r.title}
                        </td>
                        <td title={new Date(r.created_at).toLocaleString()}>
                          {new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                        </td>
                        <td>{r.step_id || (r.kind === "suggestion" ? "idea" : "-")}</td>
                        <td>
                          {r.status === "filed" && r.github_issue_url ? (
                            <>
                              <a href={r.github_issue_url} target="_blank" rel="noreferrer">
                                #{r.github_issue_number}
                              </a>{" "}
                              {r.github_state === "closed" ? "closed" : "open"}
                            </>
                          ) : r.status === "failed" ? (
                            "saved, sending later"
                          ) : (
                            "saved"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className={block.elem("actions").toClassName()}>
              <Button type="button" look="outlined" size="small" onClick={() => setView("new")}>
                New report
              </Button>
              <Button type="button" size="small" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form className={block.elem("form").toClassName()} onSubmit={submit}>
            <div className={block.elem("fields").toClassName()}>
              <div className={block.elem("row").toClassName()}>
                <label className={block.elem("field").toClassName()}>
                  <span className={block.elem("label").toClassName()}>What is this?</span>
                  <select
                    className={block.elem("select").toClassName()}
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                  >
                    {KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={block.elem("field").toClassName()}>
                  <span className={block.elem("label").toClassName()}>Step ID</span>
                  <input
                    className={block.elem("input").toClassName()}
                    value={stepId}
                    onChange={(e) => setStepId(e.target.value)}
                    placeholder="e.g. AN-NER-07 from your checklist"
                    autoComplete="off"
                  />
                </label>
              </div>

              <label className={block.elem("field").toClassName()}>
                <span className={block.elem("label").toClassName()}>Title</span>
                <div className={block.elem("title-wrap").toClassName()}>
                  <span className={block.elem("title-prefix").toClassName()} title="Added automatically">
                    {pageName}:
                  </span>
                  <input
                    ref={titleRef}
                    className={block.elem("input").toClassName()}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={kind === "failure" ? "short description of the problem" : "your idea in a few words"}
                    maxLength={160}
                  />
                </div>
              </label>

              <div
                className={block
                  .elem("row")
                  .mod({ three: kind === "failure" })
                  .toClassName()}
              >
                <label className={block.elem("field").toClassName()}>
                  <span className={block.elem("label").toClassName()}>Module</span>
                  <select
                    className={block.elem("select").toClassName()}
                    value={module}
                    onChange={(e) => setModule(e.target.value)}
                  >
                    {modules.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={block.elem("field").toClassName()}>
                  <span className={block.elem("label").toClassName()}>Where</span>
                  <select
                    className={block.elem("select").toClassName()}
                    value={surface}
                    onChange={(e) => setSurface(e.target.value)}
                  >
                    {surfaces.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
                {kind === "failure" && (
                  <label className={block.elem("field").toClassName()}>
                    <span className={block.elem("label").toClassName()}>How bad is it?</span>
                    <select
                      className={block.elem("select").toClassName()}
                      value={severity}
                      onChange={(e) => setSeverity(e.target.value)}
                    >
                      {severities.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label.split(" — ")[0]}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              <div
                className={block
                  .elem("row")
                  .mod({ single: kind !== "failure" })
                  .toClassName()}
              >
                <label className={block.elem("field").toClassName()}>
                  <span className={block.elem("label").toClassName()}>
                    {kind === "failure" ? "What I did" : "What I was trying to do"}
                  </span>
                  <textarea
                    className={block.elem("textarea").toClassName()}
                    rows={3}
                    style={{ height: 68 }}
                    value={did}
                    onChange={(e) => setDid(e.target.value)}
                    placeholder="the clicks / typing, in order"
                  />
                </label>
                {kind === "failure" && (
                  <label className={block.elem("field").toClassName()}>
                    <span className={block.elem("label").toClassName()}>What I expected</span>
                    <textarea
                      className={block.elem("textarea").toClassName()}
                      rows={3}
                      style={{ height: 68 }}
                      value={expected}
                      onChange={(e) => setExpected(e.target.value)}
                      placeholder="the 'You should see' text, or what you expected"
                    />
                  </label>
                )}
              </div>
              <label className={block.elem("field").toClassName()}>
                <span className={block.elem("label").toClassName()}>
                  {kind === "failure" ? "What happened" : "Suggestion"}
                </span>
                <textarea
                  className={block.elem("textarea").toClassName()}
                  rows={3}
                  style={{ height: 68 }}
                  value={happened}
                  onChange={(e) => setHappened(e.target.value)}
                  placeholder={
                    kind === "failure"
                      ? "what you saw instead (error text, wrong value, nothing…)"
                      : "what would make this better"
                  }
                />
              </label>
            </div>

            <div className={block.elem("actions").mod({ split: true }).toClassName()}>
              <div className={block.elem("shot-row").toClassName()}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => takeShot(e.target.files?.[0])}
                />
                {shotUrl ? (
                  <>
                    <img className={block.elem("shot-thumb").toClassName()} src={shotUrl} alt="Screenshot to attach" />
                    <Button
                      type="button"
                      look="outlined"
                      size="small"
                      onClick={() => {
                        setShot(null);
                        setShotUrl("");
                      }}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    look="outlined"
                    size="small"
                    onClick={() => fileRef.current?.click()}
                    title="Or paste an image into the form (Ctrl/Cmd+V)"
                  >
                    Add screenshot
                  </Button>
                )}
              </div>
              <div className={block.elem("actions-right").toClassName()}>
                <Button type="button" look="outlined" size="small" onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" size="small" waiting={busy} disabled={busy}>
                  Send report
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
